import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { createChatSurface } from "./chat-surface";

interface ChatPhoneProps {
  animate: boolean;
  className?: string;
}

/**
 * A lightweight, non-WebGL phone that plays the same live chat as the 3D
 * model. Used on mobile, on software renderers and with reduced motion.
 */
export default function ChatPhone({ animate, className }: ChatPhoneProps) {
  const screenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) return;

    const surface = createChatSurface({ animate, scale: 0.75 });
    surface.canvas.className = "block h-full w-full";
    surface.canvas.setAttribute("aria-hidden", "true");
    screen.appendChild(surface.canvas);

    let frameId: number | null = null;
    const loop = (now: number) => {
      surface.tick(now);
      frameId = window.requestAnimationFrame(loop);
    };

    // Only run the loop while the phone is visible.
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && animate && frameId === null) {
        frameId = window.requestAnimationFrame(loop);
      } else if (!entry?.isIntersecting && frameId !== null) {
        window.cancelAnimationFrame(frameId);
        frameId = null;
      }
    });
    observer.observe(screen);

    return () => {
      observer.disconnect();
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      surface.canvas.remove();
    };
  }, [animate]);

  return (
    <div className={cn("[perspective:1400px]", className)} aria-hidden="true">
      <div
        className={cn(
          "relative h-full aspect-[9/19.4] [transform-style:preserve-3d] [transform:rotateY(-14deg)_rotateX(6deg)_rotateZ(3deg)]",
          animate && "animate-[phone-float_7s_ease-in-out_infinite]"
        )}
      >
        {/* Glow cast by the screen */}
        <div className="absolute -inset-[18%] -z-10 rounded-full bg-[radial-gradient(closest-side,rgba(20,156,255,0.35),rgba(20,156,255,0.08)_60%,transparent)] blur-2xl" />

        {/* Titanium frame */}
        <div className="relative h-full w-full rounded-[13%/6%] bg-[linear-gradient(135deg,#3B4A5C_0%,#161E29_28%,#0B1119_55%,#26333F_80%,#DEFF77_130%)] p-[2.4%] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9),-18px_0_40px_-24px_rgba(20,156,255,0.8),inset_0_0_0_1px_rgba(255,255,255,0.12)]">
          <div className="relative h-full w-full overflow-hidden rounded-[11.5%/5.3%] bg-black p-[1.6%]">
            <div
              ref={screenRef}
              className="h-full w-full overflow-hidden rounded-[10.5%/4.9%] bg-[#0A1119]"
            />
            {/* Notch */}
            <div className="absolute left-1/2 top-[1.6%] h-[3.1%] w-[42%] -translate-x-1/2 rounded-b-[40%] bg-black" />
            {/* Glass sheen */}
            <div className="pointer-events-none absolute inset-0 rounded-[11.5%/5.3%] bg-[linear-gradient(115deg,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0.03)_32%,transparent_46%)]" />
          </div>
        </div>

        {/* Side buttons */}
        <span className="absolute -left-[1.2%] top-[18%] h-[6%] w-[1.4%] rounded-l bg-[#2A3747]" />
        <span className="absolute -left-[1.2%] top-[27%] h-[9%] w-[1.4%] rounded-l bg-[#2A3747]" />
        <span className="absolute -right-[1.2%] top-[24%] h-[12%] w-[1.4%] rounded-r bg-[#2A3747]" />
      </div>
    </div>
  );
}
